/**
 * 사진 액자 — 벽에 걸거나 세우는 액자. 벽에서 젖혀 뒤를 볼 수 있다.
 */

import * as THREE from 'three';
import { bevelBox } from './kernel';
import { type Piece } from './pieces';

// ---------------------------------------------------------------------------
// 로컬 유틸 — shrineTextures.ts 의 것과 같은 기법이지만 그쪽이 export 하지 않아
// 그대로 옮겨 쓴다 (모듈 간 결합을 늘리지 않기 위해 의도적으로 중복).
// ---------------------------------------------------------------------------

function mulberry32(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function canvas(w: number, h: number) {
  const cv = document.createElement('canvas');
  cv.width = w;
  cv.height = h;
  return { cv, ctx: cv.getContext('2d')! };
}

function toTexture(cv: HTMLCanvasElement) {
  const t = new THREE.CanvasTexture(cv);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 8;
  return t;
}

/** 종이 바탕 얼룩 — 여러 텍스처가 공유하는 잔 얼룩 채우기 */
function paperStains(ctx: CanvasRenderingContext2D, w: number, h: number, rnd: () => number, n = 30) {
  for (let i = 0; i < n; i++) {
    ctx.fillStyle = `rgba(140,128,100,${rnd() * 0.045})`;
    const r = rnd() * w * 0.16 + 6;
    ctx.beginPath();
    ctx.arc(rnd() * w, rnd() * h, r, 0, Math.PI * 2);
    ctx.fill();
  }
}

/**
 * 액자 앞면 — 강만신과 젊은 여자. 얼굴을 사실적으로 그리려 하면 procedural로는
 * 실패하니(uncanny), shinDoTexture 식으로 실루엣만 추상화한다: 어두운 도포의
 * 나이 든 인물(왼) + 밝은 저고리에 긴 머리의 젊은 인물(오)로 구도만 구별한다.
 */
function familyPhotoTexture(w = 384, h = 480): THREE.CanvasTexture {
  const { cv, ctx } = canvas(w, h);
  const rnd = mulberry32(7701);
  ctx.fillStyle = '#cdb187';
  ctx.fillRect(0, 0, w, h);
  const grad = ctx.createRadialGradient(w / 2, h * 0.42, h * 0.12, w / 2, h * 0.5, h * 0.62);
  grad.addColorStop(0, 'rgba(255,244,220,0.22)');
  grad.addColorStop(1, 'rgba(55,38,20,0.38)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);

  // 강만신 — 어두운 도포, 나이 든 실루엣
  const manX = w * 0.36;
  ctx.fillStyle = '#332621';
  ctx.beginPath();
  ctx.moveTo(manX - w * 0.12, h * 0.86);
  ctx.lineTo(manX - w * 0.15, h * 0.5);
  ctx.quadraticCurveTo(manX, h * 0.4, manX + w * 0.15, h * 0.5);
  ctx.lineTo(manX + w * 0.12, h * 0.86);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = '#caa87f';
  ctx.beginPath();
  ctx.ellipse(manX, h * 0.34, w * 0.07, h * 0.055, 0, 0, Math.PI * 2);
  ctx.fill();

  // 젊은 여자 — 밝은 저고리 + 긴 머리로 나이/성별 대비를 준다
  const womanX = w * 0.64;
  ctx.fillStyle = '#8a3b3f';
  ctx.beginPath();
  ctx.moveTo(womanX - w * 0.11, h * 0.86);
  ctx.lineTo(womanX - w * 0.13, h * 0.52);
  ctx.quadraticCurveTo(womanX, h * 0.44, womanX + w * 0.13, h * 0.52);
  ctx.lineTo(womanX + w * 0.11, h * 0.86);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = '#241c16';
  ctx.beginPath();
  ctx.ellipse(womanX, h * 0.35, w * 0.076, h * 0.09, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#e8c9a4';
  ctx.beginPath();
  ctx.ellipse(womanX, h * 0.37, w * 0.058, h * 0.045, 0, 0, Math.PI * 2);
  ctx.fill();

  // 인화지 특유의 스크래치
  for (let i = 0; i < 40; i++) {
    ctx.strokeStyle = `rgba(255,250,235,${rnd() * 0.12})`;
    ctx.lineWidth = 0.6 + rnd();
    const x = rnd() * w;
    const y = rnd() * h;
    const len = 6 + rnd() * 30;
    const a = rnd() * Math.PI;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + Math.cos(a) * len, y + Math.sin(a) * len);
    ctx.stroke();
  }
  ctx.strokeStyle = '#f2ead9';
  ctx.lineWidth = w * 0.05;
  ctx.strokeRect(0, 0, w, h);
  return toTexture(cv);
}

/**
 * 액자 뒷면 — 오행·방위 부적 양식의 정답지. 한자만 쓰면 퍼즐이 막히므로
 * 색 글자 자체를 한글로 적는다(白 대신 "백"을 흰 잉크로). 백(白)은 종이 위에서
 * 흰 글자가 안 보이므로 얇은 외곽선을 둘러 읽히게 한다.
 */
function ohaengAnswerTexture(w = 320, h = 420): THREE.CanvasTexture {
  const { cv, ctx } = canvas(w, h);
  const rnd = mulberry32(9042);
  ctx.fillStyle = '#ddcf9e';
  ctx.fillRect(0, 0, w, h);
  paperStains(ctx, w, h, rnd, 26);

  const rows: [string, string][] = [
    ['청', '동쪽'],
    ['적', '남쪽'],
    ['황', '중앙'],
    ['백', '서쪽'],
    ['흑', '북쪽'],
  ];
  const colorFor: Record<string, string> = {
    청: '#2f5fa8',
    적: '#a82b24',
    황: '#c98f1e',
    백: '#f7f4ea',
    흑: '#141210',
  };
  const rowH = h / (rows.length + 1);
  rows.forEach(([ch, dir], i) => {
    const y = rowH * (i + 1);
    ctx.font = '700 46px "Noto Sans KR", system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    if (ch === '백') {
      ctx.lineWidth = 3;
      ctx.strokeStyle = '#2a241c';
      ctx.strokeText(ch, w * 0.32, y);
    }
    ctx.fillStyle = colorFor[ch];
    ctx.fillText(ch, w * 0.32, y);
    ctx.font = '400 24px "Noto Sans KR", system-ui, sans-serif';
    ctx.fillStyle = '#2a241c';
    ctx.textAlign = 'left';
    ctx.fillText(`( ${dir} )`, w * 0.44, y);
  });
  ctx.strokeStyle = 'rgba(60,44,20,0.5)';
  ctx.lineWidth = w * 0.02;
  ctx.strokeRect(w * 0.02, h * 0.02, w * 0.96, h * 0.96);
  return toTexture(cv);
}

// ---------------------------------------------------------------------------
// FRAME_PIECES
// ---------------------------------------------------------------------------

export const FRAME_PIECES: Piece[] = [

  {
    id: 'sajin_aekja',
    /** 벽에서 젖혀 뒤를 본다 — 액자 뒤는 숨기기 좋은 자리다 (2026-09-16) */
    openable: [{ part: '액자', swing: 1.1, axis: 'y', pivot: [-0.17, 0.22, 0.0] }],
    mount: 'wall',
    // Tripo GLB(`sajin_aekja.glb`)는 깊이가 0.73m 라 벽 두께(3.5cm)에 맞추면 6cm 로 쪼그라들어
    // 아예 안 보였다 — 절차본이 사진·뒤판·정답 종이까지 있으니 그걸 쓴다 (2026-09-16)
    name: '사진 액자',
    collection: 'sahol',
    category: '접견실',
    /* 사건 메모: 강만신과 젊은 여자의 사진. 뒤판과 마운트보드 사이에 오행·방위 정답지가 끼워져 있다.
       정답표의 색 글자는 한자가 아니라 한글로 적어 퍼즐이 막히지 않게 했다. */
    note: '벽에 걸린 사진 액자. 사진·마운트보드·뒤판으로 층을 쌓았고, 뒤판과 마운트보드 사이에 끼운 종이를 뒤판보다 살짝 크고 한쪽으로 치우치게 잘라 뒤에서 보면 귀가 삐져나온다 — "뒤를 들출 수 있다"는 것이 실루엣만으로 보이게 하기 위해서다. 살짝 기울여 걸어 오래 걸린 액자로 읽히고, 벽에서 젖혀(openable) 뒤를 볼 수 있다.',
    hangs: true,
    dims: { w: 0.34, h: 0.44, t: 0.035 },
    build(w, d) {
      // 조각 전체가 하나의 여닫이 부품 — 벽에서 젖혀 뒤를 본다 (2026-09-16)
      w.movable('액자', () => {
      const tilt = 0.055; // 완벽히 수평이면 오래 걸린 액자로 안 읽힌다 — 살짝 기울여 건다
      w.at(
        0,
        0,
        0,
        () => {
          const frameT = d.w * 0.09;
          const borderD = d.t * 0.34;
          const bevel = Math.min(0.003, frameT / 3);
          const front = -d.t / 2;

          w.part('액자 테두리');
          const edgeSpecs: [number, number, number, number][] = [
            [0, -frameT / 2, d.w + frameT * 2, frameT], // 위
            [0, -d.h + frameT / 2, d.w + frameT * 2, frameT], // 아래
            [-d.w / 2 + frameT / 2, -d.h / 2, frameT, d.h - frameT * 2], // 왼쪽
            [d.w / 2 - frameT / 2, -d.h / 2, frameT, d.h - frameT * 2], // 오른쪽
          ];
          for (const [ex, ey, ew, eh] of edgeSpecs) {
            const g = bevelBox(ew, eh, borderD, bevel);
            g.translate(ex, ey, front + borderD / 2);
            w.add(g, 'ebony', 0.08);
          }

          w.part('마운트보드');
          const boardW = d.w - frameT * 1.6;
          const boardH = d.h - frameT * 1.6;
          const backT = d.t * 0.16;
          const mountD = d.t - borderD - backT;
          const mount = bevelBox(boardW, boardH, mountD, bevel);
          mount.translate(0, -d.h / 2, front + borderD + mountD / 2);
          w.add(mount, 'ebony', 0.15);

          w.thinSurface(() => {
            w.part('사진');
            const photo = new THREE.PlaneGeometry(boardW * 0.92, boardH * 0.92);
            photo.rotateY(Math.PI); // 기본은 +Z를 보므로 정면(-Z)에 놓으려면 뒤집어야 한다
            photo.translate(0, -d.h / 2, front + borderD - 0.0006); // 마운트보드 앞면보다 앞 — 안쪽이면 보드에 묻혀 검게 보인다 (2026-09-16)
            w.add(photo, 'painted', 0);

            w.part('정답 종이');
            // 마운트보드보다 크고 한쪽으로 치우치게 잘라, 뒤판 밖으로 귀가 삐져나오게 한다
            const answerSheet = new THREE.PlaneGeometry(boardW * 1.08, boardH * 1.06);
            answerSheet.translate(
              d.w * 0.02,
              -d.h / 2 + d.h * 0.015,
              front + borderD + mountD - 0.0004,
            );
            w.add(answerSheet, 'paper', 0);
          });

          w.part('뒤판');
          const back = bevelBox(boardW * 0.98, boardH * 0.98, backT, bevel);
          back.translate(0, -d.h / 2, front + borderD + mountD + backT / 2);
          w.add(back, 'ebony', 0.15);
        },
        0,
        0,
        tilt,
      );
    });
    },
    materials(renderer) {
      void renderer;
      return {
        painted: new THREE.MeshStandardMaterial({
          map: familyPhotoTexture(),
          roughness: 0.65,
          side: THREE.DoubleSide,
        }),
        paper: new THREE.MeshStandardMaterial({
          map: ohaengAnswerTexture(),
          roughness: 0.9,
          side: THREE.DoubleSide,
        }),
      };
    },
  },
];
